/**
 * @module Utils
 *
 */
import { Promise, resolve } from "rsvp";
import { Observable, Subscriber, Subscription, ReplaySubject } from "rxjs";
import { S3, AWSError } from "aws-sdk";
import { isNone } from "app/utils";
import { fetchPost } from "app/utils/request";

interface FileData {
  [key: string]: any;
  url?: string;
}

interface ProgressData extends S3.ManagedUpload.Progress {
  data?: FileData;
}

export interface SubscriberPromise {
  isAborted: boolean;
  abort: () => void;
  onProgress?: (progress: ProgressData) => void;
  subscribe: (
    progress: (d: ProgressData) => void,
    error: (e: AWSError) => void,
    complete: () => void,
  ) => Subscription;
  promise: Promise<any>;
  then: (
    value: (v: FileData) => FileData,
    onRejected?: (e: AWSError) => any,
  ) => Promise<FileData>;
  catch: (
    value: (v: AWSError) => Promise<AWSError>,
  ) => Promise<FileData | AWSError>;
  finally: (value: () => void) => void;
}

export interface UploaderObject {
  s3Uploader: S3.ManagedUpload;
  config: FileData;
}

/**
 *
 * @method upload
 * @param blob {Blod|File}
 * @param name {string}
 * @param type {string}
 * @param resourceType {string}
 * @return {Observer}
 */
export default function upload(
  blob: File | Blob,
  name: string,
  type: string,
  resourceType: string,
): SubscriberPromise {
  const response: SubscriberPromise = {
    isAborted: false,
    onProgress: null,
    abort() {
      response.isAborted = true;
    },
    subscribe: null,
    promise: resolve(),
    then: (fn: (v: FileData) => FileData) => resolve().then(fn),
    catch: (fn: (v: AWSError) => Promise<AWSError>) =>
      resolve().catch((error) => {
        console.log("error", error);
        if (
          error.code === "RequestAbortedError" ||
          error.message === "UPLOAD_CANCELLED"
        ) {
          return resolve();
        }
        return fn(error);
      }),
    finally: (fn: () => void) => {
      return resolve().finally(() => {
        response.isAborted = false;
        fn();
      });
    },
  };

  let promiseObject: Promise<FileData> = resolve();
  const subscriber = new Observable((scribe: Subscriber<ProgressData>) => {
    promiseObject = new Promise<FileData>((resolve, reject) => {
      createS3Upload(blob, name, type, resourceType).then((result) => {
        const s3Uploader = result.s3Uploader;
        const config = result.config;

        if (!response.isAborted) {
          onProgress(scribe, s3Uploader, response);
          response.abort = onAbort(scribe, s3Uploader, response);

          const errorFunc = (err: AWSError) => {
            if (err.code === "RequestAbortedError" || response.isAborted) {
              reject(new Error("UPLOAD_CANCELLED"));
              return;
            }
            scribe.error(err);
            reject(err);
          };

          const successFunc = (data: S3.ManagedUpload.SendData) => {
            if (response.isAborted) {
              reject(new Error("UPLOAD_CANCELLED"));
              return;
            }

            const dout: FileData = Object.assign({}, config, {
              url: data.Location,
            });
            scribe.next({
              total: (s3Uploader as any).totalBytes,
              loaded: (s3Uploader as any).totalBytes,
              data: dout,
            });
            scribe.complete();
            resolve(dout);
          };

          s3Uploader.send(
            (error: AWSError, data: S3.ManagedUpload.SendData) => {
              if (response.isAborted) {
                reject(new Error("UPLOAD_CANCELLED"));
                return;
              }

              if (!isNone(error)) {
                errorFunc(error);
              } else {
                successFunc(data);
              }
            },
          );
        } else {
          reject(new Error("UPLOAD_CANCELLED"));
        }
      })
        // Without this the promise never settles when the presign request
        // (POST /uploads) fails, so callers awaiting it hang forever rather
        // than showing an error.
        .catch((error) => {
          reject(error);
        });
    });
  });

  const subject = new ReplaySubject<ProgressData>();
  subscriber.subscribe({
    next: (val) => subject.next(val),
    error: (err) => subject.error(err),
    complete: () => subject.complete(),
  });

  response.subscribe = (
    progress: (d: ProgressData) => void,
    error: (e: AWSError) => void,
    complete: () => void,
  ): Subscription => subject.subscribe(progress, error, complete);

  response.promise = promiseObject;

  // Forwards the rejection handler too, so `await upload(...)` settles on
  // failure. Awaiting a thenable calls then(onFulfilled, onRejected); dropping
  // the second argument left the await pending forever on a failed upload.
  response.then = (d, onRejected?) => {
    return promiseObject.then(d, onRejected);
  };

  response.catch = (d) => {
    return promiseObject.catch(d);
  };

  response.finally = (d) => {
    promiseObject.finally(d);
  };

  return response;
}

function onProgress(
  subscriber: Subscriber<ProgressData>,
  s3Uploader: S3.ManagedUpload,
  response: SubscriberPromise,
): void {
  const initialProgress = { total: (s3Uploader as any).totalBytes, loaded: 0 };
  subscriber.next(initialProgress);

  if (response.onProgress) {
    response.onProgress(initialProgress);
  }

  s3Uploader.on("httpUploadProgress", (e: S3.ManagedUpload.Progress) => {
    const progressData = { total: e.total, loaded: e.loaded };
    subscriber.next(progressData);

    if (response.onProgress) {
      response.onProgress(progressData);
    }
  });
}

function onAbort(
  subscriber: Subscriber<ProgressData>,
  s3Uploader: S3.ManagedUpload,
  response: SubscriberPromise,
): () => void {
  return function abort(): void {
    response.isAborted = true;
    if (s3Uploader && s3Uploader.abort) {
      s3Uploader.abort();
    }
    subscriber.unsubscribe();
  };
}

/**
 * get S3 config from server
 *
 * @method getConfig
 * @param files {object[]}
 * @return {Promise}
 */
function getConfig(files: FileData[]): Promise<FileData> {
  return fetchPost("uploads", { files }, { version: 1 });
}

/**
 * create an instance of the s3 client to send files to
 *
 * @method createS3Upload
 * @param blob {Blob|File}
 * @param name {string}
 * @param type {string}
 * @param resourceType {string}
 */
function createS3Upload(
  blob: File | Blob,
  name: string,
  type: string,
  resourceType: string = "resource",
): Promise<UploaderObject> {
  const configParams = {
    filename: name,
    type: resourceType,
  };

  return getConfig([configParams]).then((conf) => {
    const fileConf = conf.files[0];

    // create s3 client instance
    const client = new S3({
      region: conf.region,
      useAccelerateEndpoint: true,
      credentials: {
        accessKeyId: conf.accessKey,
        secretAccessKey: conf.secretKey,
        sessionToken: conf.sessionToken,
      },
    });

    // create upload request
    const request = client.upload({
      Bucket: fileConf.bucket,
      Key: fileConf.key,
      Body: blob,
      ACL: "public-read",
      ContentType: type,
    });

    return { s3Uploader: request, config: fileConf };
  });
}
